import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAllCompaniesService } from "../../services/Company/CompanyServices";
import {
  deleteOrderService,
  getAllordersService,
  updateOrderService,
} from "../../services/Order/OrderServices";
import { getAllproductsService } from "../../services/Product/ProductServices";
import { showError, showSuccess } from "../Toast/Toast.functions";
import AdminPageHeader from "./AdminPageHeader";

interface NamedRef {
  _id: string;
  name: string;
}

interface AdminOrder {
  _id: string;
  description: string;
  companyBuyer: NamedRef;
  products: NamedRef[];
}

const AdminOrders = () => {
  const { t } = useTranslation(["common"]);
  const toast = useRef<Toast>(null);

  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [companies, setCompanies] = useState<NamedRef[]>([]);
  const [products, setProducts] = useState<NamedRef[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [editingOrder, setEditingOrder] = useState<AdminOrder | null>(null);
  const [deletingOrder, setDeletingOrder] = useState<AdminOrder | null>(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      getAllordersService(),
      getAllCompaniesService(),
      getAllproductsService(),
    ])
      .then(([ordersRes, companiesRes, productsRes]: any[]) => {
        setOrders(ordersRes.data ?? []);
        setCompanies(companiesRes.data ?? []);
        setProducts(productsRes.data ?? []);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filteredOrders = orders.filter((order) => {
    const search = globalFilterValue.trim().toLowerCase();
    if (!search) return true;
    return (
      order.description?.toLowerCase().includes(search) ||
      order.companyBuyer?.name?.toLowerCase().includes(search)
    );
  });

  const submitEdit = () => {
    if (!editingOrder) return;
    updateOrderService({
      _id: editingOrder._id,
      description: editingOrder.description,
      companyBuyer: editingOrder.companyBuyer._id,
      products: editingOrder.products.map((p) => p._id),
      // Leaving the notify payload untouched: this only corrects the order's own data.
      notify: null,
    })
      .then(() => {
        showSuccess(toast, t("common:Save"));
        setEditingOrder(null);
        loadAll();
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  const confirmDelete = () => {
    if (!deletingOrder) return;
    deleteOrderService({ _id: deletingOrder._id })
      .then(() => {
        setOrders((prev) => prev.filter((o) => o._id !== deletingOrder._id));
        setDeletingOrder(null);
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  return (
    <div className="admin-page">
      <Toast ref={toast} position="top-right" />
      <AdminPageHeader title={t("common:orders")} />

      <div className="admin-page-body">
      <div className="p-input-icon-left mb-3">
        <i className="pi pi-search" />
        <InputText
          value={globalFilterValue}
          onChange={(e) => setGlobalFilterValue(e.target.value)}
          placeholder="Keyword Search"
        />
      </div>

      <DataTable
        value={filteredOrders}
        loading={loading}
        paginator
        rows={20}
        dataKey="_id"
        emptyMessage={t("common:noResults")}
      >
        <Column
          header={t("common:companies")}
          body={(row: AdminOrder) => row.companyBuyer?.name}
        />
        <Column field="description" header={t("common:request")} />
        <Column
          header={t("common:products")}
          body={(row: AdminOrder) => (row.products ?? []).map((p) => p.name).join(", ")}
        />
        <Column
          body={(row: AdminOrder) => (
            <div className="admin-row-actions">
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text"
                onClick={() => setEditingOrder({ ...row })}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-warning"
                onClick={() => setDeletingOrder(row)}
              />
            </div>
          )}
        />
      </DataTable>
      </div>

      <Dialog
        header={t("common:order")}
        visible={editingOrder != null}
        onHide={() => setEditingOrder(null)}
        style={{ width: "40vw" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setEditingOrder(null)}>
              {t("common:undo")}
            </Button>
            <Button className="general-button" onClick={submitEdit}>
              {t("common:Save")}
            </Button>
          </div>
        }
      >
        {editingOrder && (
          <div className="p-fluid">
            <div className="field">
              <label>{t("common:request")}</label>
              <InputTextarea
                rows={4}
                value={editingOrder.description}
                onChange={(e) =>
                  setEditingOrder({ ...editingOrder, description: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:companies")}</label>
              <Dropdown
                value={editingOrder.companyBuyer?._id}
                options={companies.map((c) => ({ value: c._id, label: c.name }))}
                onChange={(e) =>
                  setEditingOrder({
                    ...editingOrder,
                    companyBuyer: companies.find((c) => c._id === e.value) as NamedRef,
                  })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:products")}</label>
              <MultiSelect
                value={editingOrder.products.map((p) => p._id)}
                options={products.map((p) => ({ value: p._id, label: p.name }))}
                onChange={(e) =>
                  setEditingOrder({
                    ...editingOrder,
                    products: products.filter((p) => e.value.includes(p._id)),
                  })
                }
                display="chip"
              />
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        onHide={() => setDeletingOrder(null)}
        visible={deletingOrder != null}
        closable={false}
        style={{ maxWidth: "50vh" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setDeletingOrder(null)}>
              {t("common:No")}
            </Button>
            <Button className="general-button" onClick={confirmDelete}>
              {t("common:Yes")}
            </Button>
          </div>
        }
      >
        {deletingOrder?.description}
      </Dialog>
    </div>
  );
};

export default AdminOrders;
